<script>
  import { createEventDispatcher } from 'svelte';
  import { fade, scale } from 'svelte/transition';
  import { FontAwesomeIcon } from '@fortawesome/svelte-fontawesome';
  import { faBell, faFileLines, faXmark } from '@fortawesome/pro-light-svg-icons';

  export let event = null;
  export let isOpen = false;
  export let timezone = 'UTC';

  const dispatch = createEventDispatcher();

  function closeModal() {
    dispatch('close');
  }

  function formatTimestamp(timestamp) {
    return new Date(timestamp).toLocaleString('en-US', {
      timeZone: event?.timezone || timezone,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
    });
  }

  function formatContentItemValue(value) {
    if (value instanceof Date && !Number.isNaN(value.getTime())) {
      return formatTimestamp(value);
    }
    if (typeof value === 'number' && Number.isFinite(value)) {
      const ms = value > 1e12 ? value : value > 1e9 ? value * 1000 : null;
      if (ms != null && !Number.isNaN(new Date(ms).getTime())) {
        return formatTimestamp(ms);
      }
    }
    if (typeof value === 'string') {
      const s = value.trim();
      if (/^\d{10,13}$/.test(s)) {
        const n = Number(s);
        const ms = n > 1e12 ? n : n * 1000;
        if (!Number.isNaN(new Date(ms).getTime())) {
          return formatTimestamp(ms);
        }
      }
      if (/^\d{4}-\d{2}-\d{2}/.test(s) || /[zZ]|[+-]\d{2}:?\d{2}$/.test(s)) {
        const d = new Date(s);
        if (!Number.isNaN(d.getTime())) {
          return formatTimestamp(s);
        }
      }
    }
    return value;
  }

  function getEventIcon(kind) {
    return kind === 'form' ? faFileLines : faBell;
  }

  function getEventTone(kind) {
    return kind === 'form'
      ? 'bg-sky-100 text-sky-700 dark:bg-sky-500/20 dark:text-sky-200'
      : 'bg-amber-100 text-amber-700 dark:bg-amber-500/20 dark:text-amber-100';
  }
</script>

{#if isOpen && event}
  <!-- svelte-ignore a11y-click-events-have-key-events -->
  <!-- svelte-ignore a11y-no-static-element-interactions -->
  <div
    class="fixed inset-0 z-50 flex items-center justify-center px-4 backdrop-blur-sm"
    on:click={closeModal}
    transition:fade={{ duration: 200 }}
  >
    <div class="absolute inset-0 bg-slate-800 opacity-75"></div>

    <div
      class="relative z-60 w-full max-w-2xl overflow-hidden rounded-2xl bg-white shadow-2xl dark:bg-gray-900"
      on:click|stopPropagation
      transition:scale={{ duration: 250, start: 0.96 }}
    >
      <div class="flex items-start justify-between gap-4 border-b border-stone-200 px-5 py-4 dark:border-gray-700">
        <div class="flex items-start gap-4">
          <div class={`flex h-12 w-12 items-center justify-center rounded-xl ${getEventTone(event.kind)}`}>
            <FontAwesomeIcon icon={getEventIcon(event.kind)} class="text-lg" />
          </div>

          <div>
            <div class="mb-2 flex flex-wrap items-center gap-2">
              <span class="rounded-full bg-stone-100 px-2.5 py-1 text-[11px] font-medium uppercase tracking-wide text-gray-600 dark:bg-gray-800 dark:text-gray-300">
                {event.typeLabel}
              </span>
              <span class="text-xs text-gray-500 dark:text-gray-400">{event.sourceLabel}</span>
            </div>

            <h2 class="text-lg font-semibold text-gray-900 dark:text-white">{event.title}</h2>
            <p class="mt-1 text-sm text-gray-500 dark:text-gray-400">
              {formatTimestamp(event.timestamp)}
            </p>
          </div>
        </div>

        <button
          type="button"
          class="text-gray-400 transition-colors hover:text-gray-700 dark:hover:text-gray-200"
          on:click={closeModal}
          aria-label="Close event details"
        >
          <FontAwesomeIcon icon={faXmark} class="text-lg" />
        </button>
      </div>

      <!-- <div class="grid gap-3 px-5 py-4 md:grid-cols-2">
        <div class="rounded-xl bg-stone-50 p-4 dark:bg-gray-800/70">
          <p class="mb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
            Event type
          </p>
          <p class="text-sm text-gray-800 dark:text-gray-100">{event.typeLabel}</p>
        </div>

        <div class="rounded-xl bg-stone-50 p-4 dark:bg-gray-800/70">
          <p class="mb-1 text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
            Source
          </p>
          <p class="text-sm text-gray-800 dark:text-gray-100">{event.sourceLabel}</p>
        </div>
      </div> -->

      <div class="space-y-4 px-5 pt-4 pb-5">
        <!-- <div>
          <p class="mb-2 text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
            Summary
          </p>
          <div class="rounded-xl border border-stone-200 bg-stone-50 px-4 py-3 text-sm leading-6 whitespace-pre-wrap text-gray-700 dark:border-gray-700 dark:bg-gray-800/70 dark:text-gray-200">
            {event.summary || event.description || 'No additional event summary is available.'}
          </div>
        </div> -->

        {#if event.contentItems && event.contentItems.length > 0}
          <div>
            <p class="mb-2 text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
              Content
            </p>
            <div class="overflow-hidden rounded-xl border border-stone-200 dark:border-gray-700">
              <table class="min-w-full border-collapse table-auto">
                <tbody>
                  {#each event.contentItems as item, index}
                    <tr
                      class={`text-sm ${
                        index % 2 === 0
                          ? 'bg-stone-50 dark:bg-gray-800/70'
                          : 'bg-white dark:bg-gray-900'
                      }`}
                    >
                      <td class="w-1/3 px-4 py-2 font-medium text-gray-600 dark:text-gray-300">
                        {item.label}
                      </td>
                      <td class="px-4 py-2 whitespace-pre-wrap text-gray-800 dark:text-gray-100">
                        {formatContentItemValue(item.value)}
                      </td>
                    </tr>
                  {/each}
                </tbody>
              </table>
            </div>
          </div>
        {/if}

        {#if event.description && event.description !== event.summary}
          <div>
            <p class="mb-2 text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">
              Notes
            </p>
            <div class="rounded-xl border border-stone-200 bg-stone-50 px-4 py-3 text-sm leading-6 whitespace-pre-wrap text-gray-700 dark:border-gray-700 dark:bg-gray-800/70 dark:text-gray-200">
              {event.description}
            </div>
          </div>
        {/if}
      </div>
    </div>
  </div>
{/if}
